import { v4 as uuidv4 } from 'uuid';
import { queryClient } from 'utils/reactQueryClient';
import {
  createDevice,
  createFamily,
  DeviceConfig,
  DeviceData,
  Family,
  LinkJapanVendor,
  updateDevice,
} from './usePatient';

/**
 * Gets the first family ID, or creates one if no families exist
 */
export async function getOrCreateFamilyId(
  families: Family[] | undefined,
): Promise<string> {
  if (!families || families.length === 0) {
    // Create a new family if none exists
    const newFamily = await createFamily({ name: 'my home' });
    // Invalidate families query to refresh the list
    await queryClient.invalidateQueries(['families']);
    return newFamily.id;
  }
  return families[0].id;
}

/**
 * Creates a new device with the given configuration
 */
export async function createDeviceWithConfig(
  config: DeviceConfig,
  familyId: string,
  patientInfo: any,
): Promise<void> {
  const deviceName = config.deviceName || 'Device';
  const roomId = null;

  const deviceData: Partial<DeviceData> = {
    name: deviceName,
    familyId,
    roomId,
    vendor: LinkJapanVendor,
    pid: config.pid,
    did: uuidv4(),
    extraInfo: {
      ...patientInfo,
      notification: config.notificationConfig,
    },
  };

  await createDevice(deviceData);

  // Invalidate device queries to refresh the data
  await queryClient.invalidateQueries([config.queryKey]);
}

/**
 * Updates or creates a device based on whether it exists
 * If device exists, updates it. If not, creates a new one.
 */
export async function updateOrCreateDevice(
  devices: DeviceData[] | undefined,
  config: DeviceConfig,
  familyId: string,
  patientInfo: any,
): Promise<void> {
  if (devices && devices.length > 0) {
    // Update existing device
    const device = devices[0];
    await updateDevice(device.deviceId, {
      extraInfo: {
        ...patientInfo,
        notification: config.notificationConfig,
      },
    });
  } else {
    // Create new device
    await createDeviceWithConfig(config, familyId, patientInfo);
  }

  // Invalidate device queries to refresh the data
  await queryClient.invalidateQueries([config.queryKey]);
}

/**
 * Ensures a device exists for the given configuration
 * Creates it if it doesn't exist, but doesn't update if it does
 */
export async function ensureDeviceExists(
  devices: DeviceData[] | undefined,
  config: DeviceConfig,
  families: Family[] | undefined,
  patientInfo: any,
): Promise<void> {
  if (!devices || devices.length === 0) {
    const familyId = await getOrCreateFamilyId(families);
    await createDeviceWithConfig(config, familyId, patientInfo);
  }
}

